import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRevalidator } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import debug from 'debug';

// GQL
import { CLOSE_ORDER, UPDATE_ORDER } from '../gql/mutations/orders';
import { VALIDATE_SURVEILLANCE_ROUTE } from '../gql/mutations/surveillanceRoute';
import { GET_ORDER_BY_ID } from '../gql/queries/orders/orders';
import { GET_SURVEILLANCE_ROUTE_CARD_BY_ORDER_ID } from '../gql/queries/surveillanceRoute';
import { GET_ACTION_EXPEDITION_CLOSE_INFO } from '../gql/queries/actions/actions';
import {
  evictDerivedCaches,
  syncOrderStateToTwinEntityCache,
} from '../gql/cacheHelpers';
import { OrderState, OrderType } from '../types/orders';

// Redux
import { openModal, closeModal } from '../state/actions/ui/modal';
import { modalIds } from '../components/modals/SuperModal/types';
import {
  orderCloseDecline,
  orderCloseClearPending,
} from '../state/actions/ui/orderCloseConfirmation';
import {
  getPendingCloseConfirmations,
  pickPendingCloseConfirmation,
  type CloseOrderConfirmationScope,
} from '../state/selectors/ui/orderCloseConfirmation';

// Utils
import { showSuccessToast, showBackendErrorToast } from '../utils/toasts';
import { isOfflineQueuedError } from '../offline/isOfflineQueuedError';
import {
  decideCloseOrderPrompt,
  decideFromExpeditionCheck,
  type ExpeditionCloseCheck,
  type ParentOrderLookup,
} from '../utils/closeOrderPrompt';

const logT99 = debug('rois:close-order:t99');

type ParentOrderInfo = {
  roid?: string;
  type?: string;
  description?: string;
  state?: OrderState;
  indexExpedition?: number;
};

/**
 * T99 — Escucha las notas pendientes del aviso (Redux, una por orden) y abre el
 * modal de cerrar-OT en las pantallas que montan este hook. Es **per-pantalla**
 * (no global): cada pantalla decide montarlo, y el modal salta al aterrizar ahí
 * tras validar (mismo patrón que ya tenían ShowOT y ActionsValidation, ahora
 * factorizado y extendido a Gestión Horaria).
 *
 * `scope` dice qué nota puede enseñar la pantalla: el detalle de una orden
 * (`{ kind: 'order', orderId }`) solo la suya; las pantallas generales
 * (`{ kind: 'any' }`, por defecto) la más reciente. Al contestar una, la
 * siguiente sale sola. Spec 2026-10-12-aviso-cierre-recorrido § 12.
 *
 * Resuelve la info del Order padre (roid/type/description) por `parentOrderId`
 * para que el modal identifique QUÉ OT se cierra. Es clave desde Gestión
 * Horaria, donde el `parentRoid` no viaja en el pending → sin esto el modal
 * saldría sin ROID y el usuario no sabría qué OT cierra.
 *
 * Al cerrar invalida los caches derivados (informe Gestión Horaria + listas) vía
 * `evictDerivedCaches` y revalida → la pantalla destino refresca sin "Actualizar".
 */
export function useCloseOrderConfirmation(
  scope: CloseOrderConfirmationScope = { kind: 'any' }
): void {
  const dispatch = useDispatch();
  const client = useApolloClient();
  const { revalidate } = useRevalidator();
  const { t } = useTranslation(['validation', 'modals']);
  const [closeOrderMutation] = useMutation(CLOSE_ORDER);
  const [updateOrderMutation] = useMutation(UPDATE_ORDER);
  const [validateRouteMutation] = useMutation(VALIDATE_SURVEILLANCE_ROUTE);
  // Primitivas, no el objeto `scope`: llega nuevo en cada render.
  const scopeKind = scope.kind;
  const scopeOrderId = scope.kind === 'order' ? scope.orderId : undefined;
  const pendingCloseConfirmation = useSelector((state) =>
    pickPendingCloseConfirmation(
      getPendingCloseConfirmations(state),
      scopeKind === 'order'
        ? { kind: 'order', orderId: scopeOrderId }
        : { kind: 'any' }
    )
  );

  /**
   * Sin red la cola guarda el cambio y lo reproduce al volver: para quien
   * pulsó es un éxito diferido, no un error (mismo trato que ChangeStateModal).
   * La nota se borra: si al reproducir ya no aplica, el servidor lo rechaza y
   * la orden sigue como estaba, sin aviso huérfano.
   */
  const settleQueued = useCallback(
    (orderId: string) => {
      showSuccessToast(t('modals:changeState.queuedOffline'));
      dispatch(closeModal());
      dispatch(orderCloseClearPending(orderId));
      revalidate();
    },
    [t, dispatch, revalidate]
  );

  const parentOrderId = pendingCloseConfirmation?.parentOrderId;
  const triggeredAt = pendingCloseConfirmation?.triggeredAt;
  const pendingRoid = pendingCloseConfirmation?.parentRoid;

  /**
   * Validar el Recorregut de Vigilància — el gesto equivalente a «cerrar la
   * OT» cuando el contenedor es un recorrido.
   *
   * `CLOSE_ORDER` no sirve aquí: la máquina de estados del RV no se pilota con
   * el validador genérico (mismo motivo por el que `ChangeStateModal` tiene su
   * rama SR). Y la mutation quiere el id de la ENTIDAD `SurveillanceRoute`,
   * no el de su Order gemela — entidad doble.
   */
  const handleValidateRoute = useCallback(
    async (routeId: string, routeOrderId: string, closeActions = false) => {
      try {
        logT99('[T99] Validando recorrido:', routeId, { closeActions });
        await validateRouteMutation({ variables: { id: routeId, closeActions } });

        evictDerivedCaches(client.cache);

        showSuccessToast(
          t('validation:routeValidated', "El recorregut s'ha validat correctament")
        );
        dispatch(closeModal());
        dispatch(orderCloseClearPending(routeOrderId));
        revalidate();
      } catch (error) {
        if (isOfflineQueuedError(error)) {
          settleQueued(routeOrderId);
          return;
        }
        console.error('[T99] Error validando el recorrido:', error);
        // Código estable del backend (p.ej. SR_ACTIONS_NOT_VALIDATED cuando
        // quedan básicas sin validar) → mensaje útil en vez de un fallo mudo.
        showBackendErrorToast(
          error,
          t('validation:routeValidateError', 'No s\'ha pogut validar el recorregut')
        );
        dispatch(closeModal());
      }
    },
    [validateRouteMutation, client, t, dispatch, revalidate, settleQueued]
  );

  const handleConfirmCloseOrder = useCallback(
    async (orderId: string, includeActions = false) => {
      try {
        logT99('[T99] Closing order:', orderId, { includeActions });
        await closeOrderMutation({ variables: { orderId, includeActions } });

        // Cerrar la OT cambia su estado → invalida informe Gestión Horaria +
        // listas (incl. laborHoursReport) para que refresquen al caer en
        // cualquier pantalla destino (orden, tabla de validación o informe).
        evictDerivedCaches(client.cache);

        showSuccessToast(t('validation:orderClosed', "L'ordre s'ha tancat correctament"));
        dispatch(closeModal());
        dispatch(orderCloseClearPending(orderId));
        // Refresca la pantalla donde haya caído el redirect (orden, tabla de
        // validación o informe de Gestión Horaria).
        revalidate();
      } catch (error) {
        if (isOfflineQueuedError(error)) {
          settleQueued(orderId);
          return;
        }
        console.error('[T99] Error closing order:', error);
        // Mismo aviso que su hermano `handleValidateRoute`: el backend
        // REVALIDA al recibir el cierre, así que este rechazo es esperable en
        // el uso real (dos personas en la misma expedición — mientras A tiene
        // el modal abierto, B marca una actuación como pendiente de certificar
        // y el botón deja de estar autorizado: `ORDERS_ACTIONS_PENDING_CERTIFICATION`).
        // Sin toast el modal se cerraba en silencio y A se iba creyendo que
        // había cerrado la OT.
        showBackendErrorToast(
          error,
          t('validation:errors.unexpectedError', 'Ha ocorregut un error inesperat')
        );
        dispatch(closeModal());
      }
    },
    [closeOrderMutation, client, t, dispatch, revalidate, settleQueued]
  );

  /**
   * Marca la OT padre como "pendiente de validar A.T" (asistencia técnica) en
   * lugar de cerrarla. Reutiliza UPDATE_ORDER con el estado `pending-at-validation`
   * (ya soportado en backend para Generic/Campaign; los roles que llegan a este
   * modal hacen bypassWorkflow). Mismo cierre de flujo que confirmar el cierre:
   * invalida caches derivados, toast, cierra modal, limpia el pending y revalida.
   */
  const handleSetPendingAT = useCallback(
    async (orderId: string) => {
      try {
        logT99('[T99] Marcando OT como pendiente de A.T:', orderId);
        const { data: updatedOrderData } = await updateOrderMutation({
          variables: {
            input: { id: orderId, state: OrderState.PendingATValidation },
          },
        });

        // Igual que en ChangeStateModal: UPDATE_ORDER solo devuelve la
        // Order, y el backend sincroniza Order->entidad EN SERVIDOR — la
        // caché del cliente deja `GenericOrder:<id>`/`Campaign:<id>` con el
        // estado viejo sin esto (ver gql/cacheHelpers.ts).
        syncOrderStateToTwinEntityCache(
          client.cache,
          updatedOrderData?.updateOrder,
        );

        // Cambiar el estado de la OT invalida informe Gestión Horaria + listas.
        evictDerivedCaches(client.cache);

        showSuccessToast(
          t('validation:orderPendingAT', "L'ordre s'ha marcat com a pendent d'A.T")
        );
        dispatch(closeModal());
        dispatch(orderCloseClearPending(orderId));
        revalidate();
      } catch (error) {
        if (isOfflineQueuedError(error)) {
          settleQueued(orderId);
          return;
        }
        console.error('[T99] Error marcando pendiente de A.T:', error);
        // Antes se cerraba el modal en silencio y quien pulsó creía que la OT
        // había quedado pendiente de A.T. La nota se queda para reintentar.
        showBackendErrorToast(error, t('modals:changeState.errors.generic'));
        dispatch(closeModal());
      }
    },
    [updateOrderMutation, client, t, dispatch, revalidate, settleQueued]
  );

  useEffect(() => {
    if (!parentOrderId) return;

    let cancelled = false;

    const openWithOrderInfo = async () => {
      // Leer el padre para (1) identificar QUÉ OT se cierra y (2) decidir con su
      // estado ACTUAL. Red primero (autoritativa): no nos fiamos de una caché
      // que pueda mostrar la OT abierta cuando ya está cerrada. La caché solo si
      // la red falla (p. ej. backend caído en mitad de un deploy).
      let lookup: ParentOrderLookup<ParentOrderInfo>;
      try {
        const res = await client.query<{ order: ParentOrderInfo | null }>({
          query: GET_ORDER_BY_ID,
          variables: { id: parentOrderId },
          fetchPolicy: 'network-only',
        });
        lookup = res.data?.order
          ? { status: 'found', order: res.data.order }
          : { status: 'missing' };
      } catch {
        let cached: ParentOrderInfo | undefined;
        try {
          cached =
            client.readQuery<{ order: ParentOrderInfo }>({
              query: GET_ORDER_BY_ID,
              variables: { id: parentOrderId },
            })?.order ?? undefined;
        } catch {
          cached = undefined;
        }
        lookup = cached
          ? { status: 'found', order: cached }
          : { status: 'unreachable' };
      }
      if (cancelled) return;

      // Abrir, descartar o reintentar. Incluye el corte de bucle de siempre (OT
      // ya cerrada: el bucle del deploy en prod) y la regla del recorrido (solo
      // «Pendiente de validar»). Spec 2026-10-12-aviso-cierre-recorrido § 8.
      const decision = decideCloseOrderPrompt(lookup);
      if (decision.action === 'retry-later') {
        logT99(
          '[T99] No se pudo leer la OT %s — no abro; se reintenta en la próxima pantalla',
          parentOrderId
        );
        return;
      }
      if (decision.action === 'discard') {
        logT99(
          '[T99] OT %s descartada (%s) — limpio pending y no abro modal',
          parentOrderId,
          decision.reason
        );
        dispatch(orderCloseClearPending(parentOrderId));
        return;
      }
      const { order } = decision;

      // Contenedor Recorregut de Vigilància: mismo empujón que en la OT
      // genérica, pero el gesto es VALIDAR el recorrido, no cerrarlo, y hace
      // falta el id de la ENTIDAD (la ruta y la mutation lo piden; lo que
      // tenemos es la Order gemela — entidad doble).
      const isRouteContainer = order?.type === OrderType.SurveillanceRoute;
      let routeId: string | null = null;
      if (isRouteContainer) {
        try {
          const { data } = await client.query({
            query: GET_SURVEILLANCE_ROUTE_CARD_BY_ORDER_ID,
            variables: { orderId: parentOrderId },
            fetchPolicy: 'cache-first',
          });
          routeId = data?.surveillanceRouteByOrderId?.id ?? null;
        } catch {
          routeId = null;
        }
        // Sin id de entidad no hay nada que ofrecer: mejor no abrir el modal
        // que abrirlo con un botón que fallaría.
        if (!routeId) {
          logT99('[T99] Recorrido sin id de entidad resoluble — no abro modal');
          dispatch(orderCloseClearPending(parentOrderId));
          return;
        }
      }
      if (cancelled) return;

      // Comprobación de última hora contra el servidor: ¿sigue todo terminado?
      // y ¿cabe ofrecer «cerrar también las actuaciones»? Se pregunta AQUÍ, al
      // abrir el modal, y no en los disparadores: el dato tiene que ser fresco
      // (entre apuntar la nota y abrir esto puede haber pasado de todo: otro
      // usuario, la cola sin red al volver la conexión…) y así hay un solo
      // punto que mantener en vez de tres. Spec 2026-10-12 § 12.
      const indexExpedition = order?.indexExpedition;
      let check: ExpeditionCloseCheck = { status: 'not-applicable' };
      // Truthy NO vale: el expediente 0 existe (primer expediente Generic/Campaign
      // del tenant) y `0` es falsy en JS — hay que comprobar presencia, no verdad.
      if (indexExpedition !== undefined && indexExpedition !== null) {
        try {
          const { data } = await client.query({
            query: GET_ACTION_EXPEDITION_CLOSE_INFO,
            variables: { indexExpedition: Number(indexExpedition) },
            fetchPolicy: 'network-only',
          });
          check = {
            status: 'answered',
            allActionsValidated: data?.actionExpeditionCloseInfo?.allActionsValidated,
            canCloseActions: data?.actionExpeditionCloseInfo?.canCloseActions,
          };
        } catch {
          check = { status: 'unreachable' };
        }
      }
      if (cancelled) return;

      const expeditionDecision = decideFromExpeditionCheck(check);
      if (expeditionDecision.action === 'retry-later') {
        logT99(
          '[T99] Sin red para comprobar la OT %s — no abro; se reintenta en la próxima pantalla',
          parentOrderId
        );
        return;
      }
      if (expeditionDecision.action === 'discard') {
        logT99(
          '[T99] OT %s ya no tiene todo terminado — limpio pending y no abro modal',
          parentOrderId
        );
        dispatch(orderCloseClearPending(parentOrderId));
        return;
      }
      const { canCloseActions } = expeditionDecision;

      logT99('[T99] Opening close-order modal — parentOrderId:', parentOrderId);
      dispatch(
        openModal(modalIds.CONFIRM_CLOSE_ORDER_MODAL, {
          parentOrderId,
          parentRoid: order?.roid ?? pendingRoid,
          orderType: order?.type,
          orderDescription: order?.description,
          // Estado ACTUAL de la OT (el mismo `order` que acaba de leerse de red
          // para el loop-breaker). El modal lo pinta con el chip canónico para
          // que quien valida desde Gestión Horaria sepa en qué estado está sin
          // navegar hasta la OT.
          orderState: order?.state,
          routeId,
          onConfirmClose: () =>
            isRouteContainer && routeId
              ? handleValidateRoute(routeId, parentOrderId)
              : handleConfirmCloseOrder(parentOrderId),
          onConfirmCloseWithActions: canCloseActions
            ? () =>
                isRouteContainer && routeId
                  ? handleValidateRoute(routeId, parentOrderId, true)
                  : handleConfirmCloseOrder(parentOrderId, true)
            : undefined,
          // «Pendent d'A.T» es un estado administrativo de cobro que NO existe
          // en la máquina del recorrido — ofrecerlo sería un botón que no
          // puede hacer nada. El modal ya lo pinta condicionalmente.
          onSetPendingAT: isRouteContainer
            ? undefined
            : () => handleSetPendingAT(parentOrderId),
          onCancelClose: () => {
            logT99('[T99] User declined to close order:', parentOrderId);
            dispatch(orderCloseDecline(parentOrderId));
            dispatch(closeModal());
          },
          isLoading: false,
        })
      );
    };

    openWithOrderInfo();

    return () => {
      cancelled = true;
    };
  }, [
    parentOrderId,
    triggeredAt,
    pendingRoid,
    client,
    dispatch,
    handleConfirmCloseOrder,
    handleSetPendingAT,
    handleValidateRoute,
  ]);
}
